import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { Equipamento, PaginatedResponse } from '../types'
import type { EquipamentoFormData } from '../features/equipamentos/schema'

interface EquipamentoListParams {
  search?: string
  // Pós-refator: filtra por Empresa (papel CLIENTE) via `?empresa=<uuid>`.
  // `site` mantido pra compat com master pré-merge.
  empresa?: string
  area?: string
  site?: string
  tipo?: string
  ativo?: boolean | 'todos'
  status_operacional?: string
  ordering?: string
  page?: number
  page_size?: number
}

export function useEquipamentoList(params?: EquipamentoListParams) {
  return useQuery({
    queryKey: ['equipamentos', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<Equipamento>>('/equipamentos/', { params })
      return r.data
    },
    staleTime: 1000 * 60,
  })
}

export function useEquipamentoDetalhe(id: string) {
  return useQuery({
    queryKey: ['equipamentos', id],
    queryFn: async () => {
      const r = await api.get<Equipamento>(`/equipamentos/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export function useEquipamentoCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: EquipamentoFormData) => api.post<Equipamento>('/equipamentos/', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipamentos'] }),
  })
}

export function useEquipamentoUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<EquipamentoFormData>) =>
      api.patch<Equipamento>(`/equipamentos/${id}/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['equipamentos'] })
      qc.invalidateQueries({ queryKey: ['equipamentos', id] })
    },
  })
}

export function useEquipamentoDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/equipamentos/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipamentos'] }),
  })
}

export interface ImportLinha {
  linha: number
  acao: 'criar' | 'ignorar_duplicado' | 'erro'
  erro: string | null
  dados: Record<string, unknown>
}

export interface ImportColunaMap {
  coluna: number
  header_planilha: string
  campo_sistema: string | null
  reconhecida: boolean
}

export interface ImportResultado {
  total_linhas: number
  a_criar: number
  duplicados: number
  erros: number
  linhas: ImportLinha[]
  sites_resolvidos: Record<string, string>
  mapeamento_colunas: ImportColunaMap[]
  avisos: string[]
  dry_run: boolean
  commit?: { criados: number; areas_criadas: number; ids: string[] }
}

export interface ImportPayload {
  arquivo: File
  // Pós-refator atores-e-automacao: chave passou a ser `cliente_id`
  // (Empresa com papel CLIENTE). Mantemos `site_id` opcional pra compat
  // com master pré-merge — o backend ignora a chave que não usa.
  cliente_id?: string
  site_id?: string
  dry_run: boolean
}

export function useEquipamentoImportar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, cliente_id, site_id, dry_run }: ImportPayload) => {
      const fd = new FormData()
      fd.append('arquivo', arquivo)
      if (cliente_id) fd.append('cliente_id', cliente_id)
      if (site_id) fd.append('site_id', site_id)
      fd.append('dry_run', dry_run ? 'true' : 'false')
      const r = await api.post<ImportResultado>('/equipamentos/importar/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data
    },
    onSuccess: (data) => {
      if (!data.dry_run) qc.invalidateQueries({ queryKey: ['equipamentos'] })
    },
  })
}

export function downloadTemplateEquipamentos() {
  return api
    .get('/equipamentos/importar/template/', { responseType: 'blob' })
    .then((r) => {
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'template-equipamentos-pmoc123.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    })
}

export interface EtiquetasQRParams {
  empresa?: string
  ids?: string[]
  formato: 'a4_3x8' | 'zebra_50x30'
}

export async function downloadEtiquetasQR({ empresa, ids, formato }: EtiquetasQRParams) {
  const r = await api.get('/equipamentos/etiquetas-qr/', {
    params: { empresa, ids: ids?.join(','), formato },
    responseType: 'blob',
  })
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `etiquetas-equipamentos-${formato}.pdf`
  a.click()
  URL.revokeObjectURL(url)
}
